// Imports for external libraries go here.
import { FC, useState, useEffect } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import { Link } from '@marriott/mi-ui-library';
// Imports for internal (to the monorepo) libraries go here.
import { LanguageSelector } from '../../../molecules/LanguageSelector';
import { StyledHeader } from './Header.styles';
import { HeaderProps, HeaderMenuItem, LangLinkRegion, LangLinkLanguage, LangType } from './Header.types';
import DOMPurify from 'isomorphic-dompurify';
import logo from '../../../assets/images/lovetravels/alt-logo.svg';
import Image from 'next/image';

export const LTHeader: FC<HeaderProps> = (props: HeaderProps) => {
  const { headerLinks, langLinks } = props.model;

  const transformedLangNavList = langLinks?.reduce(
    (
      acc: Record<string, Record<string, { linkLabel: string; linkURL: string; openInNewTab: boolean }[]>>,
      regionObj: LangLinkRegion,
      index: number
    ) => {
      const columnKey = `col${index + 1}`;

      acc[columnKey] = {
        [regionObj.region]: regionObj.languages.map((lang: LangLinkLanguage) => ({
          linkLabel: lang.linkTitle,
          linkURL: lang.linkPath,
          openInNewTab: lang.openInNewTab || false,
        })),
      };
      return acc;
    },
    {}
  );

  const mappedLanguageSelectorModal = {
    languageSelectorTitle: 'Select Language',
    langNavList: transformedLangNavList,
    ':type': 'mi-aem-martech/components/lovetravels/language-selector',
  };

  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState(false);
  const isAuthorMode = AuthoringUtils.isInEditor();

  const [isUnicefPage, setIsUnicefPage] = useState(false);
  const [backLinkHref, setBackLinkHref] = useState('https://www.marriott.com/');

  const [isFontLoaded, setIsFontLoaded] = useState(false);
  const [currentLangLabel, setCurrentLangLabel] = useState('English');

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const isUnicef = document.body.classList.contains('lt-unicef-page');
      setIsUnicefPage(isUnicef);

      if (isUnicef && typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        // extracting page extension
        const extensionMatch = currentPath.match(/\.[^/.]+$/);
        const extension = extensionMatch ? extensionMatch[0] : '';
        // getting the parent page
        const parentBasePath = currentPath.substring(0, currentPath.lastIndexOf('/'));
        // building final parent path
        const parentPath = parentBasePath === '/' ? '/' : `${parentBasePath}${extension}`;
        setBackLinkHref(parentPath);
      }

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          setIsFontLoaded(true);
        });
      } else {
        setIsFontLoaded(true);
      }

      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        let matchedLabel = 'English';

        if (mappedLanguageSelectorModal.langNavList) {
          Object.values(mappedLanguageSelectorModal.langNavList).forEach((col: Record<string, LangType[]>) => {
            Object.values(col).forEach((regionLangs: LangType[]) => {
              const matchedLang = regionLangs.find(
                (lang: LangType) =>
                  currentPath === lang.linkURL ||
                  currentPath === `${lang.linkURL}.html` ||
                  currentPath === `${lang.linkURL}/` ||
                  currentPath === `${lang.linkURL}.mi`
              );
              if (matchedLang && matchedLang.linkLabel) {
                matchedLabel = matchedLang.linkLabel;
              }
            });
          });
        }
        setCurrentLangLabel(matchedLabel || 'English');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const backLinkText = isUnicefPage ? 'Back to Marriott Love Travels' : 'Back to Marriott Bonvoy';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const toggleKeyHandler = (event: any) => {
    if (event.key === 'Enter') {
      setIsLanguageSelectorOpen(!isLanguageSelectorOpen);
    }
  };
  const toggleHandler = () => {
    setIsLanguageSelectorOpen(!isLanguageSelectorOpen);
  };

  const logoImage = <Image priority src={logo} alt="LoveTravels" className="logo" width={180} height={50} />;

  return (
    <div id="universal-header" style={{ fontFamily: 'var(--font-sans-serif, Helvetica, Arial, sans-serif)' }}>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .lt-header .logo { margin-left: 20px !important; width: 140px !important; height: 39px !important; }
        @media (min-width: 768px) {
          .lt-header .logo { margin-left: 35px !important; width: 180px !important; height: 50px !important; }
        }
      `,
        }}
      />
      <StyledHeader
        className="lt-header"
        data-testid="lt-header"
        isAuthorMode={isAuthorMode}
        data-component-name="o-account-header"
        style={{
          overflow: 'hidden',
          top: 0,
          position: isAuthorMode ? 'static' : 'fixed',
          width: isAuthorMode ? 'auto' : '100vw',
          zIndex: 1000,
        }}
      >
        <div
          className="lt-back"
          style={{
            backgroundColor: '#FF9662',
            textAlign: 'center',
            fontSize: '12px',
            lineHeight: '16px',
            padding: '7px 64px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <div className="left-container" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <a
              href={backLinkHref}
              className="custom_click_track"
              data-custom_click_track_value={`Header|${backLinkText}|Link-BackToMarriottBonvoy`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                color: '#000',
                textDecoration: 'none',
                opacity: isFontLoaded ? 1 : 0,
                transition: 'opacity 0.2s ease-in',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                width="7.41"
                height="12"
                style={{ transform: 'rotate(-180deg)' }}
              >
                <defs>
                  <path id="a" d="M0 10.59 4.58 6 0 1.41 1.41 0l6 6-6 6z" />
                </defs>
                <use xlinkHref="#a" fill="#000" fillRule="nonzero" />
              </svg>
              <span style={{ textDecoration: 'underline' }}>{backLinkText}</span>
            </a>
          </div>
          {mappedLanguageSelectorModal && Object.keys(mappedLanguageSelectorModal.langNavList || {}).length > 0 && (
            <div
              className="right-container"
              style={{ opacity: isFontLoaded ? 1 : 0, transition: 'opacity 0.2s ease-in' }}
            >
              <a
                data-component-name="a-ui-library-Button"
                data-testid=""
                className="custom_click_track language-selector-button-footer"
                onKeyDown={toggleKeyHandler}
                onClick={toggleHandler}
                aria-label=""
                tabIndex={0}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M7.99992 1.33331C9.66744 3.15888 10.6151 5.528 10.6666 7.99998C10.6151 10.472 9.66744 12.8411 7.99992 14.6666M7.99992 1.33331C6.3324 3.15888 5.38475 5.528 5.33325 7.99998C5.38475 10.472 6.3324 12.8411 7.99992 14.6666M7.99992 1.33331C4.31802 1.33331 1.33325 4.31808 1.33325 7.99998C1.33325 11.6819 4.31802 14.6666 7.99992 14.6666M7.99992 1.33331C11.6818 1.33331 14.6666 4.31808 14.6666 7.99998C14.6666 11.6819 11.6818 14.6666 7.99992 14.6666M1.6666 5.99998H14.3333M1.66659 9.99998H14.3333"
                    stroke="black"
                    strokeWidth="1.36751"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{currentLangLabel}</span>
              </a>
              <LanguageSelector
                currentLanguage={currentLangLabel}
                toggleHandler={toggleHandler}
                setIsOpen={setIsLanguageSelectorOpen}
                isOpen={isLanguageSelectorOpen}
                languageSelectorModal={mappedLanguageSelectorModal}
              />
            </div>
          )}
        </div>
        <div className="lt-header-inner" style={{ padding: '10px 0 0', backgroundColor: '#fff' }}>
          <div className="container-fluid">
            <div className="row d-flex align-items-md-center">
              <div className="col-md-4">
                {isUnicefPage ? (
                  <a href={backLinkHref} aria-label="LoveTravels logo links to home page">
                    {logoImage}
                  </a>
                ) : (
                  logoImage
                )}
              </div>
              <div className="col-md-8 d-flex justify-content-md-end">
                <nav
                  className="cmp-list"
                  style={{
                    display: 'flex',
                    gap: '30px',
                    width: '100%',
                    opacity: isFontLoaded ? 1 : 0,
                    transition: 'opacity 0.2s ease-in',
                    justifyContent: 'flex-end',
                  }}
                >
                  {headerLinks?.map((item: HeaderMenuItem) => {
                    const safeTitle = item.linkTitle ? DOMPurify.sanitize(item.linkTitle) : '';
                    return (
                      <Link
                        key={item.linkTitle}
                        text={item.linkTitle}
                        linkHref={item?.linkPath}
                        linkClassName="header-link custom_click_track"
                        custom_click_track_value={`Header|${safeTitle}|Jump-${safeTitle.replace(/\s+/g, '')}`}
                        linkType="internal"
                        target="_self"
                      />
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>
        </div>
      </StyledHeader>
    </div>
  );
};

export const HeaderWrapperConfig = {
  emptyLabel: 'Header',
  isEmpty: () => true,
  resourceType: `mi-aem-martech/components/lovetravels/header`,
};

export const LTHeaderEditable = (props: HeaderProps) => {
  return (
    <EditableComponent {...props} config={HeaderWrapperConfig}>
      <LTHeader {...props} />
    </EditableComponent>
  );
};
